// React
// gcp
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';

// Components
import Header from './components/Header';
import AppRoutes from './components/AppRoutes';
import MainScreenContainer from './components/MainScreen/MainScreenContainer';
import DatabaseAlertPopup from './components/DatabaseAlertPopup';
import HeaderReportErrorPopup from './components/HeaderReportErrorPopup';
import AWatchLoadingScreen from './components/AWatchLoadingScreen';

// Hooks
import { useMainScreenData } from './hooks/useMainScreenData';
import { useLandingPageData } from './hooks/useLandingPageData';
import { useEntitySearch } from './hooks/useEntityData';
// Utils
import { initializeWithLock } from './utils/initializeManager';
import { createErrorLogger } from './utils/errorHandler';


// Constants
import { APP_CONFIG } from './constants';

// Styles
import './css/App.css';

// Initialize logger after all imports
const logger = createErrorLogger('App');

function AppContent() {
  // React Router hooks MUST be called before any early returns
  const location = useLocation();
  const navigate = useNavigate();
  const isOnMainScreen = location.pathname === '/entities';

  useEffect(() => {
    const cls = 'main-screen-scroll';
    // Body defaults to overflow-y:hidden in App.css; enable page scroll on routes that need it.
    const needsBodyScroll = isOnMainScreen || location.pathname === '/';
    if (needsBodyScroll) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [isOnMainScreen, location.pathname]);

  // Initialization state
  const [isInitialized, setIsInitialized] = useState(false);

  // Main screen state and data fetching
  const { 
    params,
    updateParams,
    entities,
    totalCount,
    loading: entitiesLoading,
    error: entitiesError,
    refresh: refreshEntities,
    data: mainScreenData
  } = useMainScreenData();
  
  // Extract values from params
  const currentPage = params?.page || 1;
  const pageSize = params?.pageSize || APP_CONFIG.PAGE_SIZE;
  const selectedEntityId = params?.entity || null;

  // Accumulated entities for infinite scroll — append on page increase, reset on filter/sort/pageSize change
  const [accumulatedEntities, setAccumulatedEntities] = useState([]);
  const prevPageRef = useRef(1);
  const prevFilterKeyRef = useRef('');

  useEffect(() => {
    if (!isOnMainScreen) return;
    const filterKey = JSON.stringify({
      filters: params?.filters,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      pageSize,
    });
    const isNewQuery = filterKey !== prevFilterKeyRef.current || currentPage === 1;

    if (isNewQuery) {
      setAccumulatedEntities(entities);
    } else if (currentPage > prevPageRef.current) {
      setAccumulatedEntities(prev => [...prev, ...entities]);
    }

    prevPageRef.current = currentPage;
    prevFilterKeyRef.current = filterKey;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, currentPage, isOnMainScreen]);

  const {
    searchEntityId,
    searchFailed,
    onSearchChange,
    validateSearchInput,
    setSearchFailure,
  } = useEntitySearch();

  const landingPageData = useLandingPageData({
    isInitialized,
    pathname: location.pathname,
  });

  const [isAlertPopupOpen, setIsAlertPopupOpen] = useState(false);
  const [isHeaderReportPopupOpen, setIsHeaderReportPopupOpen] = useState(false);
  const prevPathnameRef = useRef(location.pathname);

  const closeAlertPopup = useCallback(() => {
    setIsAlertPopupOpen(false);
  }, []);

  const closeHeaderReportPopup = useCallback(() => {
    setIsHeaderReportPopupOpen(false);
  }, []);

  useEffect(() => {
    const prev = prevPathnameRef.current;
    if (prev === '/entities' && location.pathname !== '/entities') {
      setIsAlertPopupOpen(false);
    }
    prevPathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Run initialize on mount and wait for completion
  useEffect(() => {
    setIsInitialized(false);
    initializeWithLock()
      .then(() => {
        setIsInitialized(true);
      })
      .catch((error) => {
        logger.error('Initialization failed', error);
        setIsInitialized(true);
      });
  }, []);

  // Flexible keyword search — same logic for main database search and landing header shortcut
  const handleSearchSubmit = useCallback(async (searchTextOverride) => {
    if (!validateSearchInput()) return;

    try {
      const searchText = (searchTextOverride || searchEntityId || '').trim();

      const currentFilters = params?.filters || {};
      const updatedFilters = { ...currentFilters };

      if (searchText && searchText.length > 0) {
        updatedFilters.flexible_text = searchText;
      } else {
        delete updatedFilters.flexible_text;
      }

      updateParams({
        filters: updatedFilters,
        page: 1,
        entity: null,
      });

      if (!isOnMainScreen) {
        navigate('/entities');
      }
    } catch (error) {
      logger.error('Flexible keyword search failed', error);
      setSearchFailure(error.message);
    }
  }, [
    validateSearchInput,
    searchEntityId,
    params?.filters,
    updateParams,
    isOnMainScreen,
    navigate,
    setSearchFailure,
  ]);

  // Close only the EntityFrame (do not close other modals or reset filters)
  const handleCloseEntityFrame = useCallback(() => {
    if (isOnMainScreen) {
      updateParams({ entity: null });
    }
  }, [isOnMainScreen, updateParams]);
  
  // Page change handlers
  const handlePageChange = useCallback((newPage) => {
    if (!isOnMainScreen) return;
    updateParams({ page: newPage });
  }, [isOnMainScreen, updateParams]);
  
  const handlePageSizeChange = useCallback((newSize) => {
    if (!isOnMainScreen) return;
    updateParams({ pageSize: newSize, page: 1 });
  }, [isOnMainScreen, updateParams]);

  const handleNarrativeClick = useCallback((narrative) => {
    const normalizedNarrative = String(narrative || '').trim();
    if (!normalizedNarrative) return;

    updateParams({
      filters: {
        Subjects: { in: [normalizedNarrative] },
      },
      page: 1,
      entity: null,
    });
    navigate('/entities');
  }, [updateParams, navigate]);

  // Show loading while initializing data
  if (!isInitialized) {
    return (
      <div className="fullscreen-overlay">
        <AWatchLoadingScreen message="Loading application data..." />
      </div>
    );
  }

  const renderMainContent = () => {
    return (
      <MainScreenContainer
        entitiesError={entitiesError}
        refreshEntities={refreshEntities}
        isOnMainScreen={isOnMainScreen}
        selectedEntityId={selectedEntityId}
        updateUrlParams={updateParams}
        entities={accumulatedEntities}
        entitiesLoading={entitiesLoading}
        totalCount={totalCount}
        currentPage={currentPage}
        pageSize={pageSize}
        handlePageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        searchFailed={searchFailed}
        handleCloseEntityFrame={handleCloseEntityFrame}
        searchEntityId={searchEntityId}
        onSearchChange={onSearchChange}
        onSearchSubmit={handleSearchSubmit}
        searchLoading={entitiesLoading}
        urlParams={params}
        urlFilters={params?.filters}
        entitiesData={isOnMainScreen ? mainScreenData : null}
        mainScreenLoading={isOnMainScreen ? entitiesLoading : false}
        setIsAlertPopupOpen={setIsAlertPopupOpen}
      />
    );
  };

  return (
    <div className="App">
      <Header
        searchEntityId={searchEntityId}
        onSearchChange={onSearchChange}
        onSearchSubmit={handleSearchSubmit}
        searchLoading={entitiesLoading}
        onReportErrorClick={() => setIsHeaderReportPopupOpen(true)}
      />

      <AppRoutes
        renderMainContent={renderMainContent}
        landingPageData={landingPageData}
        onLandingReportNow={() => setIsAlertPopupOpen(true)}
        onNarrativeClick={handleNarrativeClick}
      />

      <DatabaseAlertPopup isOpen={isAlertPopupOpen} onClose={closeAlertPopup} />
      <HeaderReportErrorPopup isOpen={isHeaderReportPopupOpen} onClose={closeHeaderReportPopup} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;


